// Ruting. To slag, og skillet mellom dem er en sikkerhetsgrense:
//
//   · `/registrer?kode=…` er OFFENTLIG. Ingen token, ingen sidenavigasjon, ingen
//     kall som krever innlogging. Dette er lenken som deles ut til nye
//     frivillige, og den skal virke for en som ikke har noe fra før.
//   · Alt annet krever persontoken og bor bak innloggingen.
//
// Fordi registreringen er en DYPLENKE må den ligge i stien (nginx har
// `try_files … /index.html` for nettopp det). De innloggede skjermene bytter på
// hash i stedet — de trenger ingen serverstøtte, og en hash-endring gir ingen
// full sidelast midt i en dra-og-slipp.

export const VISNINGER = ['timer', 'kanban', 'sprinter', 'sammendrag', 'inviter', 'meg'] as const;
export type Visning = (typeof VISNINGER)[number];

export const VISNING_NAVN: Record<Visning, string> = {
  timer: 'Timer',
  kanban: 'Kanban',
  sprinter: 'Sprinter',
  sammendrag: 'Sammendrag',
  inviter: 'Inviter',
  meg: 'Min side',
};

/**
 * Visninger som bare gjelder for noen. Rettigheten HÅNDHEVES i tjenesten (401/403
 * på `/lenker`); dette styrer bare om knappen står i navigasjonen — en knapp som
 * alltid ender i 403 er en knapp som lærer brukeren at verktøyet er upålitelig.
 *
 * Ruta finnes fortsatt for den som skriver `#inviter` selv: skjermen forklarer da
 * rolig hvorfor den ikke kan brukes, framfor å sende deg et annet sted i stillhet.
 */
export function krever(visning: Visning): 'kanAttestere' | null {
  return visning === 'inviter' ? 'kanAttestere' : null;
}

/** Hvilke visninger denne brukeren skal se i navigasjonen. */
export function synligeVisninger(rett: { kanAttestere: boolean }): Visning[] {
  return VISNINGER.filter((v) => krever(v) === null || rett.kanAttestere);
}

export type Rute =
  | { slag: 'registrer'; kode: string }
  | { slag: 'app'; visning: Visning };

export const STANDARDVISNING: Visning = 'timer';

/**
 * Les ruta av URL-en.
 *
 * `kode` kan være tom: en registreringslenke uten kode er en lenke noen har
 * klippet feil. Det skal registreringssiden si rolig, ikke krasje på — derfor
 * returneres ruta likevel, med tom kode.
 */
export function lesRute(pathname: string, search: string, hash: string): Rute {
  const sti = pathname.replace(/\/+$/, '') || '/';
  if (sti === '/registrer' || sti.endsWith('/registrer')) {
    let kode = '';
    try {
      kode = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search).get('kode')?.trim() ?? '';
    } catch {
      kode = '';
    }
    return { slag: 'registrer', kode };
  }
  return { slag: 'app', visning: visningAv(hash) };
}

/** `#kanban` / `#/kanban` → visningen. Ukjent hash faller til standardvisningen. */
export function visningAv(hash: string): Visning {
  const h = hash.replace(/^#\/?/, '').split('?')[0].trim().toLowerCase();
  return (VISNINGER as readonly string[]).includes(h) ? (h as Visning) : STANDARDVISNING;
}

/** Hashen en lenke til visningen skal ha. */
export function hashFor(v: Visning): string {
  return `#${v}`;
}

/**
 * Registreringslenken slik den deles ut. Speiler `registreringsUrl()` i
 * @skade/dugnad/prosjekt — samme form, så en lenke laget av tjenesten og en
 * lenke vist i UI-et aldri kan avvike.
 */
export function registreringsLenke(opphav: string, kode: string): string {
  return `${opphav.replace(/\/+$/, '')}/registrer?kode=${encodeURIComponent(kode)}`;
}
